import { JSX, Show, batch, createEffect, createMemo, on } from 'solid-js';
import {
  isPointCloudLoading,
  isPointCloudVisible,
  isTrajectoryLoading,
  isTrajectoryVisible,
  MissionDetailsSelection,
  MissionItem,
  RunDetailsSelection,
  useCapabilityRegistryContext,
  useDataContext,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { FitToViewModelCapabilityCoordinator } from '@trimble/web3d';
import { SelectionSyncCoordinator } from '@trimble/pane-sync';
import { t } from '@trimble/i18n';
import { getTrbModelName } from '@trimble/web3d-providers';
import { SortableKeyOf, SortDirection } from '@trimble/ui-components/solidjs';
import {
  SelectionTree,
  SelectionTreeProps,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import {
  FilterHandlerRecord,
  ProjectDataFilterMode,
} from './project-data.types';
import { UnreachableCaseError } from 'ts-essentials';
import pointCloudIcon from '../../assets/icons/point-cloud.svg';
import ActiveIndicatorChip from '../../components/ActiveIndicatorChip';
import { handleNodeSelectionSync, setActiveAlignment } from './tree-helpers';
import { RunStatus } from '@data-access/geospatial/mobile-mapping';
import { TreeNodeStatus } from '@trimble/ui-components/solidjs';
import { useMissionTreeDelete } from './useMissionTreeDelete';
import { useRunStatusPolling } from './useRunStatusPolling';
import { useLogger } from '@trimble/solidjs-utils';
import { LoadState } from '@trimble/data-loading';
import { createMissionFolderDescendantRefTrnsGetter } from './fx-workspace-draft-checkbox-sources';
import { fxWorkspaceDraftTrailingContent } from './fx-workspace-draft-checkboxes';
import {
  buildMissionNodes,
  MISSION_ROOT_NODE_ID,
  MissionTreeNodeData,
  MissionTreeNodeType,
} from './project-data-tree-nodes';
import { filterProjectDataNodes } from './project-data-filtering';

export {
  MissionTreeNodeType,
  type MissionTreeNodeData,
} from './project-data-tree-nodes';

export interface MissionSelectionTreeContainerProps {
  nodes?: TreeNodeDefinition<MissionTreeNodeData>[];
  childrenLoadStatus?: SelectionTreeProps<MissionTreeNodeData>['childrenLoadStatus'];
  defaultExpandIds?: string[];
  filterText?: string;
  sortBy: SortableKeyOf<TreeNodeDefinition<MissionTreeNodeData>>;
  sortDirection: SortDirection;
  filterMode?: ProjectDataFilterMode;
  onRunDetailsSelect?(run: RunDetailsSelection): void;
  onMissionDetailsSelect?(mission: MissionDetailsSelection): void;
}

const MissionSelectionTreeContainer = (
  props: MissionSelectionTreeContainerProps
): JSX.Element => {
  const { activeItems, dataState, dataLoadingService } = useDataContext();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const fxWorkspace = useFxWorkspaceContext();

  const logger = useLogger().getLogger('MissionSelectionTreeContainer');

  const {
    setMissionToDelete,
    setRunToDelete,
    deleteModals: DeleteModals,
  } = useMissionTreeDelete();
  const { handleNodeExpand } = useRunStatusPolling();

  const fitToViewCoordinator = capabilityRegistry.get(
    FitToViewModelCapabilityCoordinator
  );

  const selectionSyncCoordinator = capabilityRegistry.get(
    SelectionSyncCoordinator
  );

  const activeAlignmentId = createMemo<string | undefined>(
    () => activeItems.alignmentId ?? undefined
  );

  const isPathLoading = (alignmentTrn: string): boolean => {
    return isTrajectoryLoading(dataState, alignmentTrn);
  };

  const isPathVisible = (alignmentTrn: string): boolean => {
    return isTrajectoryVisible(dataState, alignmentTrn);
  };

  const isRunLoading = (pointCloudTrn: string): boolean => {
    return isPointCloudLoading(dataState, pointCloudTrn);
  };

  const isRunVisible = (pointCloudTrn: string): boolean => {
    return isPointCloudVisible(dataState, pointCloudTrn);
  };

  const initialExpandIds = createMemo<string[] | undefined>((prev) => {
    if (prev !== undefined) return prev;

    const missions = dataState.mission;
    if (missions.state !== LoadState.LoadComplete) return undefined;

    const activeId = activeAlignmentId();
    const expandIds: string[] = [];

    for (const mission of Object.values(missions.items)) {
      const hasActiveAlignment =
        mission.pathId != null &&
        activeId != null &&
        mission.pathId === activeId;
      const hasVisiblePath =
        mission.pathId != null && isPathVisible(mission.pathId);
      const hasVisibleRun = mission.runs.some((run) => isRunVisible(run.id));

      if (hasActiveAlignment || hasVisiblePath || hasVisibleRun) {
        expandIds.push(mission.id);
        if (hasVisibleRun) {
          expandIds.push(`runs:${mission.id}`);
        }
      }
    }

    if (expandIds.length > 0) {
      expandIds.push(MISSION_ROOT_NODE_ID);
      return expandIds;
    }

    return undefined;
  });

  const defaultExpandIds = createMemo(
    () => props.defaultExpandIds ?? initialExpandIds()
  );

  const isActiveFilterMatch = (
    node: TreeNodeDefinition<MissionTreeNodeData>
  ): boolean => {
    const type = node.data?.type;
    switch (type) {
      case MissionTreeNodeType.Run:
        return isRunVisible(node.id);
      case MissionTreeNodeType.Path:
        return (
          isPathVisible(node.id) ||
          (node.data?.mission?.runs.some((run) => isRunVisible(run.id)) ??
            false)
        );
      case MissionTreeNodeType.Mission:
      case MissionTreeNodeType.Runs:
      case undefined:
        return false;
      default:
        throw new UnreachableCaseError(type);
    }
  };

  const filterHandlers: FilterHandlerRecord<MissionTreeNodeData> = {
    all: { filter: () => true },
    active: { filter: isActiveFilterMatch },
    archived: { filter: () => false },
  };

  const nodeTypeById = createMemo<Map<string, MissionTreeNodeType>>(() => {
    const byId = new Map<string, MissionTreeNodeType>();
    const missions = dataState.mission;
    if (missions.state !== LoadState.LoadComplete) return byId;

    for (const mission of Object.values(missions.items)) {
      byId.set(mission.id, MissionTreeNodeType.Mission);
      if (mission.pathId != null) {
        byId.set(mission.pathId, MissionTreeNodeType.Path);
      }
      for (const run of mission.runs) {
        byId.set(run.id, MissionTreeNodeType.Run);
      }
    }
    return byId;
  });

  const getNodeType = (nodeId: string): MissionTreeNodeType | undefined => {
    if (nodeId.startsWith('runs:')) return MissionTreeNodeType.Runs;
    return nodeTypeById().get(nodeId);
  };

  const isNodeLoading = (nodeId: string): boolean => {
    const type = getNodeType(nodeId);
    if (!type) return false;

    switch (type) {
      case MissionTreeNodeType.Mission:
      case MissionTreeNodeType.Runs:
        return false;
      case MissionTreeNodeType.Path:
        return isPathLoading(nodeId);
      case MissionTreeNodeType.Run:
        return isRunLoading(nodeId);
      default:
        throw new UnreachableCaseError(type);
    }
  };

  const isNodeVisible = (nodeId: string): boolean => {
    const type = getNodeType(nodeId);
    if (!type) return false;

    switch (type) {
      case MissionTreeNodeType.Mission:
      case MissionTreeNodeType.Runs:
        return false;
      case MissionTreeNodeType.Path:
        return isPathVisible(nodeId);
      case MissionTreeNodeType.Run:
        return isRunVisible(nodeId);
      default:
        throw new UnreachableCaseError(type);
    }
  };

  const isNodeSelected = (nodeId: string): boolean => {
    const modelId =
      getNodeType(nodeId) === MissionTreeNodeType.Path
        ? getTrbModelName(nodeId)
        : nodeId;

    return selectionSyncCoordinator()?.selectedModelIds().has(modelId) ?? false;
  };

  const handleNodeSelect = (
    nodeId: string,
    node: TreeNodeDefinition<MissionTreeNodeData>,
    e: MouseEvent
  ): void => {
    const modelId =
      getNodeType(nodeId) === MissionTreeNodeType.Path
        ? getTrbModelName(nodeId)
        : nodeId;

    void handleNodeSelectionSync(
      modelId,
      node,
      e,
      isNodeVisible,
      selectionSyncCoordinator()
    ).catch((error) => logger.error('Error selecting node', node, error));
  };

  const isRunMissingFromProject = (pointCloudTrn: string): boolean =>
    dataState.pointCloud.state === LoadState.LoadComplete &&
    dataState.pointCloud.items[pointCloudTrn] === undefined;

  const isPathMissingFromProject = (alignmentTrn: string): boolean =>
    dataState.trajectory.state === LoadState.LoadComplete &&
    dataState.trajectory.items[alignmentTrn] === undefined;

  const baseTreeNodes = createMemo(
    (): TreeNodeDefinition<MissionTreeNodeData>[] => {
      if (props.nodes !== undefined) return props.nodes;

      const missions = dataState.mission;
      return missions.state === LoadState.LoadComplete
        ? buildMissionNodes(Object.values(missions.items))
        : [];
    }
  );

  const selectionTreeNodes = createMemo(
    (): TreeNodeDefinition<MissionTreeNodeData>[] => {
      return baseTreeNodes().map((node) => {
        const data = node.data;
        if (!data) return node;

        switch (data.type) {
          case MissionTreeNodeType.Mission: {
            const mission = data.mission;
            if (!mission) return node;

            return {
              ...node,
              contextMenuOptions: [
                ...(props.onMissionDetailsSelect
                  ? [
                      {
                        text: t('siteVisualization.sidePanelLayout.seeDetails'),
                        onClick: () => {
                          props.onMissionDetailsSelect?.({
                            missionTrn: mission.missionTrn,
                            name: mission.name,
                          });
                        },
                      },
                    ]
                  : []),
                {
                  text: t('general.delete'),
                  onClick: () =>
                    setMissionToDelete({
                      missionTrn: mission.missionTrn,
                      name: mission.name,
                    }),
                },
              ],
            } satisfies TreeNodeDefinition<MissionTreeNodeData>;
          }
          case MissionTreeNodeType.Runs: {
            const mission = data.mission;
            if (!mission) return node;

            return {
              ...node,
              contextMenuOptions: [
                {
                  text: t('mobileMapping.runs.loadAll'),
                  onClick: () => loadAllRuns(mission.id),
                  disabled: () => {
                    const loadableRuns = getLoadableRuns(mission);
                    return loadableRuns.every(
                      (run) => isRunLoading(run.id) || isRunVisible(run.id)
                    );
                  },
                },
                {
                  text: t('mobileMapping.runs.unloadAll'),
                  onClick: () => unloadAllRuns(mission.id),
                  disabled: () => {
                    const loadableRuns = getLoadableRuns(mission);
                    return loadableRuns.every(
                      (run) => !isRunLoading(run.id) && !isRunVisible(run.id)
                    );
                  },
                },
              ],
            } satisfies TreeNodeDefinition<MissionTreeNodeData>;
          }
          case MissionTreeNodeType.Run: {
            const mission = data.mission;
            const run = data.run;
            if (!mission || !run) return node;

            return {
              ...node,
              contextMenuOptions: [
                {
                  text: t('siteVisualization.sidePanelLayout.fitView'),
                  onClick: () => {
                    void fitToViewCoordinator()?.fitToView(run.id);
                  },
                  disabled: () => !isPointCloudVisible(dataState, run.id),
                },
                {
                  text: t('siteVisualization.sidePanelLayout.seeDetails'),
                  onClick: () => {
                    props.onRunDetailsSelect?.({
                      id: run.id,
                      runId: run.runId,
                      missionTrn: mission.missionTrn,
                      name: run.name,
                    });
                  },
                },
                {
                  text: t('general.delete'),
                  onClick: () =>
                    setRunToDelete({
                      runId: run.runId,
                      missionTrn: mission.missionTrn,
                      name: run.name,
                    }),
                },
              ],
              status: isRunMissingFromProject(run.id)
                ? {
                    status: 'error',
                    disabled: true,
                    message: t('mobileMapping.runs.status.missingPointCloud'),
                    messageColor: 'error',
                  }
                : mapRunStatusToTreeNodeStatus(run.status),
              visibleContent: () => (
                <Show
                  when={
                    isRunVisible(run.id) &&
                    !isRunLoading(run.id) &&
                    activeItems.pointCloudTrnId === run.id
                  }
                >
                  <ActiveIndicatorChip
                    svgIcon={pointCloudIcon}
                    tooltip={t('mobileMapping.missions.activeRunTooltip')}
                    testId="active-run-tree-icon"
                  />
                </Show>
              ),
            } satisfies TreeNodeDefinition<MissionTreeNodeData>;
          }
          case MissionTreeNodeType.Path: {
            const mission = data.mission;
            if (!mission) return node;

            const pathId = node.id;

            return {
              ...node,
              contextMenuOptions: [
                {
                  text: t('siteVisualization.sidePanelLayout.fitView'),
                  onClick: () => {
                    void fitToViewCoordinator()?.fitToView(
                      getTrbModelName(pathId)
                    );
                  },
                  disabled: () => !isTrajectoryVisible(dataState, pathId),
                },
                {
                  text: t('mobileMapping.missions.setAsActive'),
                  onClick: () => {
                    setActiveAlignment(capabilityRegistry, pathId);
                  },
                  disabled: () => !isTrajectoryVisible(dataState, pathId),
                },
              ],
              status: isPathMissingFromProject(pathId)
                ? {
                    status: 'error',
                    disabled: true,
                    message: t('mobileMapping.missions.missingPath'),
                    messageColor: 'error',
                  }
                : undefined,
              visibleContent: () => (
                <Show
                  when={
                    isPathVisible(pathId) &&
                    !isPathLoading(pathId) &&
                    activeAlignmentId() != null &&
                    pathId === activeAlignmentId()
                  }
                >
                  <ActiveIndicatorChip
                    icon="tc-icon-path-navigation"
                    tooltip={t('mobileMapping.missions.activeAlignmentTooltip')}
                    testId="active-alignment-tree-icon"
                  />
                </Show>
              ),
            } satisfies TreeNodeDefinition<MissionTreeNodeData>;
          }
          default:
            throw new UnreachableCaseError(data.type);
        }
      });
    }
  );

  const getFolderDescendantRefTrns = createMemo(() => {
    const missions = dataState.mission;
    return createMissionFolderDescendantRefTrnsGetter(
      missions.state === LoadState.LoadComplete
        ? Object.values(missions.items)
        : []
    );
  });

  const togglePathVisibility = (alignmentTrn: string): void => {
    if (isPathMissingFromProject(alignmentTrn)) return;
    if (
      isTrajectoryLoading(dataState, alignmentTrn) ||
      isTrajectoryVisible(dataState, alignmentTrn)
    ) {
      dataLoadingService.unload('trajectory', alignmentTrn);
    } else {
      dataLoadingService.load('trajectory', alignmentTrn);
    }
  };

  const toggleRunVisibility = (pointCloudTrn: string): void => {
    if (isRunMissingFromProject(pointCloudTrn)) return;
    if (
      isPointCloudLoading(dataState, pointCloudTrn) ||
      isPointCloudVisible(dataState, pointCloudTrn)
    ) {
      dataLoadingService.unload('pointCloud', pointCloudTrn);
    } else {
      dataLoadingService.load('pointCloud', pointCloudTrn);
    }
  };

  const getLoadableRuns = (mission: MissionItem): MissionItem['runs'] =>
    mission.runs.filter((run) => {
      if (isRunMissingFromProject(run.id)) return false;
      const statusProps = mapRunStatusToTreeNodeStatus(run.status);
      return !statusProps?.disabled;
    });

  const loadAllRuns = (missionId: string): void => {
    if (dataState.mission.state !== LoadState.LoadComplete) return;
    const mission = dataState.mission.items[missionId];
    if (!mission) return;

    const runsToLoad = getLoadableRuns(mission).filter(
      (run) => !isRunLoading(run.id) && !isRunVisible(run.id)
    );
    batch(() => {
      for (const run of runsToLoad) {
        dataLoadingService.load('pointCloud', run.id);
      }
    });
  };

  const unloadAllRuns = (missionId: string): void => {
    if (dataState.mission.state !== LoadState.LoadComplete) return;
    const mission = dataState.mission.items[missionId];
    if (!mission) return;

    batch(() => {
      for (const run of getLoadableRuns(mission)) {
        if (isRunLoading(run.id) || isRunVisible(run.id)) {
          dataLoadingService.unload('pointCloud', run.id);
        }
      }
    });
  };

  const cancelNodeLoad = (nodeId: string): void => {
    const type = getNodeType(nodeId);
    if (type === MissionTreeNodeType.Path && isPathLoading(nodeId)) {
      dataLoadingService.unload('trajectory', nodeId);
      return;
    }
    if (type === MissionTreeNodeType.Run && isRunLoading(nodeId)) {
      dataLoadingService.unload('pointCloud', nodeId);
    }
  };

  const toggleNodeVisibility = (nodeId: string): void => {
    const type = getNodeType(nodeId);
    if (!type) return;

    switch (type) {
      case MissionTreeNodeType.Mission:
      case MissionTreeNodeType.Runs:
        break;
      case MissionTreeNodeType.Path:
        togglePathVisibility(nodeId);
        break;
      case MissionTreeNodeType.Run:
        toggleRunVisibility(nodeId);
        break;
      default:
        throw new UnreachableCaseError(type);
    }
  };

  function mapRunStatusToTreeNodeStatus(
    status?: RunStatus
  ): TreeNodeStatus | undefined {
    switch (status) {
      case 'Created':
        return {
          status: 'processing',
          disabled: true,
          message: t('mobileMapping.runs.status.created'),
        };

      case 'ProcessingPointCloud':
        return {
          status: 'processing',
          disabled: true,
          message: t('mobileMapping.runs.status.processingPointCloud'),
        };

      case 'ProcessingImages':
        return {
          status: 'processing',
          message: t('mobileMapping.runs.status.processingImages'),
        };

      case 'FailedPointCloud':
        return {
          status: 'error',
          disabled: true,
          message: t('mobileMapping.runs.status.failedPointCloud'),
          messageColor: 'error',
        };

      case 'FailedImages':
        return {
          status: 'ready',
          message: t('mobileMapping.runs.status.failedImages'),
          messageColor: 'error',
        };

      case 'Complete':
      case undefined:
        return undefined;

      default:
        throw new UnreachableCaseError(status);
    }
  }

  const polledRunsFolderIds = new Set<string>();

  const pollRunsFolderIfNeeded = (nodeId: string): void => {
    if (!nodeId.startsWith('runs:')) return;
    if (polledRunsFolderIds.has(nodeId)) return;
    polledRunsFolderIds.add(nodeId);
    void handleNodeExpand(nodeId);
  };

  const handleRunsFolderCollapse = (nodeId: string): void => {
    if (nodeId.startsWith('runs:')) {
      polledRunsFolderIds.delete(nodeId);
    }
  };

  createEffect(
    on(
      () => [props.filterMode, props.filterText, selectionTreeNodes()] as const,
      ([filterMode, filterText, nodes]) => {
        const mode = filterMode ?? 'all';
        const text = filterText ?? '';
        if (mode === 'all' && text.trim().length === 0) {
          polledRunsFolderIds.clear();
          return;
        }

        const { expandIds } = filterProjectDataNodes(
          nodes,
          mode,
          text,
          isActiveFilterMatch
        );
        for (const id of expandIds) {
          pollRunsFolderIfNeeded(id);
        }
      }
    )
  );

  return (
    <>
      <div data-testid="mission-tree" class="content">
        <SelectionTree
          multiSelect={false}
          childrenLoadStatus={
            props.childrenLoadStatus ??
            (dataState.mission.state === LoadState.LoadComplete
              ? 'success'
              : 'pending')
          }
          nodes={selectionTreeNodes()}
          rowMode={fxWorkspace.isWorkspaceDataEditing() ? 'pick' : 'default'}
          visibleContent={(slotProps) =>
            fxWorkspaceDraftTrailingContent(
              'missions',
              slotProps.node,
              fxWorkspace.isWorkspaceDataEditing(),
              getFolderDescendantRefTrns()
            )
          }
          sortBy={props.sortBy}
          sortDirection={props.sortDirection}
          filterText={props.filterText}
          filterMode={props.filterMode}
          filterHandlers={filterHandlers}
          defaultExpandIds={defaultExpandIds()}
          isNodeLoading={isNodeLoading}
          isNodeVisible={isNodeVisible}
          onVisibilityToggle={toggleNodeVisibility}
          onCancelVisibilityToggle={cancelNodeLoad}
          onNodeExpand={pollRunsFolderIfNeeded}
          onNodeCollapse={handleRunsFolderCollapse}
          isNodeSelected={isNodeSelected}
          onNodeSelect={handleNodeSelect}
        />
      </div>
      <DeleteModals />
    </>
  );
};

export default MissionSelectionTreeContainer;
